/**
 * วันขาย (business date) ของร้าน ตัดรอบวันที่ 05:00 ตามเวลาเครื่อง
 *
 * ปัญหาที่แก้: พนักงานใหม่ไม่รู้ว่าต้องปิดร้านตอนกลางคืน กะเช้าเข้ามาก็ขายต่อในรอบเดิม
 * ใบเสร็จทั้งวันเลยไปลงวันที่ของเมื่อวาน
 *
 * กติกา:
 *   - วันขายของเวลาใด ๆ = วันที่ของ (เวลานั้น - 5 ชม.) เช่น 03:00 วันที่ 29 = วันขาย 28
 *   - เปิดร้านข้ามวัน = วันขายตอนนี้ "หลัง" วันขายของรอบที่เปิดอยู่ (เทียบที่วันที่ ไม่ได้นับชั่วโมง)
 *   - เปิดร้านช่วง 00:00-04:59 ให้คนเลือกเองว่าเป็นวันขายของเมื่อวานหรือวันใหม่
 *     (บางร้านปิดไปแล้วมีลูกค้ามาสั่งเพิ่ม ต้องเปิดต่อของเมื่อวาน / ร้านโรงแรมเปิดเช้ามืดเป็นวันใหม่)
 *   - ถ้าเลือกวันใหม่ก่อน 05:00 วันขายตอนนี้จะ "ก่อน" วันที่เปิด ห้ามถือว่าข้ามวัน
 *
 * 05:00 ไม่ใช่ 07:00 เพราะร้านโรงแรมบางร้านเปิดขายตั้งแต่ 06:00
 *
 * ไฟล์นี้ต้องเป็น pure function ไม่ import อะไรเลย เพื่อให้ scripts/test-business-date.mjs เทสต์ได้ตรง ๆ
 */

export const BUSINESS_DAY_CUTOFF_HOUR = 5;

type TimeInput = string | number | Date;

export interface OpenShopLike {
  timestamp?: string;
  businessDate?: string;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** YYYY-MM-DD ตามเวลาเครื่อง */
export function calendarDateOf(time: TimeInput): string {
  const d = new Date(time);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function businessDateOf(time: TimeInput): string {
  return calendarDateOf(new Date(time).getTime() - BUSINESS_DAY_CUTOFF_HOUR * 3600 * 1000);
}

/** วันขายของรอบที่เปิดอยู่ OPEN รุ่นเก่าที่ไม่มี businessDate ให้คิดจากเวลาเปิด */
export function openShopBusinessDate(openShop: OpenShopLike | null | undefined): string | null {
  if (!openShop?.timestamp) return null;
  return openShop.businessDate || businessDateOf(openShop.timestamp);
}

export function isOpenShopCrossDay(openShop: OpenShopLike | null | undefined, now: TimeInput): boolean {
  const opened = openShopBusinessDate(openShop);
  if (!opened) return false;
  // YYYY-MM-DD เทียบแบบ string ได้ตรง
  return businessDateOf(now) > opened;
}

/** ช่วง 00:00-04:59 ต้องให้คนเลือกวันขายเอง */
export function isBusinessDateChoiceTime(now: TimeInput): boolean {
  return new Date(now).getHours() < BUSINESS_DAY_CUTOFF_HOUR;
}

/** ตัวเลือกวันขายตอนเปิดร้าน: previous = ต่อของเมื่อวาน, next = วันใหม่ (วันที่ตามปฏิทิน) */
export function businessDateChoices(now: TimeInput): { previous: string; next: string } {
  return { previous: businessDateOf(now), next: calendarDateOf(now) };
}
